import { type ReactElement } from "react";
import { Link, useNavigate } from "@tanstack/react-router";

import { WebChat } from "../../chat";
import { IntakeForm, isIntakeEmbed } from "../embeds/IntakeForm";
import { postSalesChat } from "../lib/sales-chat";
import {
  INTENT_MAX,
  MESSAGE_MAX,
  VERTICALS,
  intentFromMessage,
} from "./verticals";

export { INTENT_MAX, intentFromMessage };

interface StartSiteAskProps {
  id?: string;
  placeholder?: string;
  showStarters?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onActiveChange?: (active: boolean) => void;
}

export function StartSiteAsk({
  id = "start-site",
  placeholder = "What kind of site do you need?",
  showStarters = false,
  expandable = false,
  expanded = false,
  onActiveChange,
}: StartSiteAskProps): ReactElement {
  const navigate = useNavigate();

  function fallbackToForm(intent?: string): void {
    void navigate({
      to: "/signup",
      search: intent ? { intent } : {},
    });
  }

  return (
    <WebChat
      composerId={id}
      placeholder={placeholder}
      idleSubmitLabel="Make my site"
      maxLength={MESSAGE_MAX}
      postTurn={(messages, handlers) =>
        postSalesChat({ messages, onDelta: handlers?.onDelta })
      }
      renderEmbed={(embed, { fieldId, onError }) =>
        isIntakeEmbed(embed) ? (
          <IntakeForm embed={embed} fieldId={fieldId} onError={onError} />
        ) : null
      }
      onUnavailable={(first) => fallbackToForm(intentFromMessage(first))}
      onEmptySubmit={() => fallbackToForm()}
      onActiveChange={onActiveChange}
      onDismiss={expandable ? () => onActiveChange?.(false) : undefined}
      expanded={expandable && expanded}
      beforeStart={
        showStarters
          ? (setDraft) => (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {VERTICALS.map((starter) => (
                  <button
                    key={starter.label}
                    type="button"
                    onClick={() => setDraft(starter.intent)}
                    className="rounded-full border border-[#d5e9f7] bg-white/55 px-3.5 py-1.5 text-xs text-[#536762] transition-colors hover:border-[#b8b9dd] hover:bg-white hover:text-[#20272b]"
                  >
                    {starter.label}
                  </button>
                ))}
              </div>
            )
          : undefined
      }
      footer={
        <p className="mt-3 text-left text-xs text-[#65736f]">
          Prefer a form?{" "}
          <Link to="/signup" className="underline hover:text-[#20272b]">
            Sign up directly
          </Link>
          .
        </p>
      }
    />
  );
}
